import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Upload, FileText, X } from 'lucide-react';
import type {
  CreateTransactionRequest,
  UpdateTransactionRequest,
  ClaimTransaction,
} from '@/api/generated/client/models';
import { dollarsToCents, centsToDollars, labelFor, dateToLocalDateString } from '@/lib/utils';

// ── Display label maps & constants ────────────────────

const PAYMENT_METHODS = ['check', 'ach', 'wire', 'other'] as const;

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  other: 'Other',
};

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  carrier_payment: 'Carrier Payment',
  supplement_payment: 'Supplement Payment',
  depreciation_release: 'Depreciation Release',
  mortgage_release: 'Mortgage Release',
  other: 'Other',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Expected',
  received: 'Received',
  deposited: 'Deposited',
  cleared: 'Cleared',
  voided: 'Void',
};

// ── Derived constants ─────────────────────────────────

const PAYMENT_TYPES = Object.keys(PAYMENT_TYPE_LABELS) as (keyof typeof PAYMENT_TYPE_LABELS)[];
const STATUS_OPTIONS = Object.keys(STATUS_LABELS) as (keyof typeof STATUS_LABELS)[];

// ── Props ─────────────────────────────────────────────

export interface AddPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateTransactionRequest, checkFile?: File) => void;
  onUpdate?: (id: number, data: UpdateTransactionRequest, checkFile?: File, removeExistingCheckFile?: boolean) => void;
  isSubmitting: boolean;
  /** If provided, the modal is in edit mode */
  editingTransaction?: ClaimTransaction | null;
}

// ── Form state ────────────────────────────────────────

interface FormState {
  transactionType: string;
  grossAmount: string;
  feeTaken: string;
  payer: string;
  payees: string;
  paymentMethod: string;
  referenceNumber: string;
  transactionDate: string;
  status: string;
  checkFile: File | null;
  /** True when the user explicitly removes the existing server-side check file. */
  removeExistingCheckFile: boolean;
}

const initialForm: FormState = {
  transactionType: 'carrier_payment',
  grossAmount: '',
  feeTaken: '',
  payer: '',
  payees: '',
  paymentMethod: '',
  referenceNumber: '',
  transactionDate: dateToLocalDateString(new Date()),
  status: 'pending',
  checkFile: null,
  removeExistingCheckFile: false,
};

/** Map a ClaimTransaction to editable form values. */
function transactionToForm(tx: ClaimTransaction): FormState {
  return {
    transactionType: tx.transactionType ?? 'carrier_payment',
    grossAmount: centsToDollars(tx.amountCents),
    feeTaken: tx.feeCents != null ? centsToDollars(tx.feeCents) : '',
    payer: tx.payer ?? '',
    payees: tx.payee ?? '',
    paymentMethod: tx.paymentMethod ?? '',
    referenceNumber: tx.referenceNumber ?? '',
    transactionDate: tx.transactionDate,
    status: tx.status ?? 'pending',
    checkFile: null,
    removeExistingCheckFile: false,
  };
}

// ── Component ─────────────────────────────────────────

export function AddPaymentModal({
  open,
  onOpenChange,
  onSubmit,
  onUpdate,
  isSubmitting,
  editingTransaction,
}: AddPaymentModalProps) {
  const isEdit = !!editingTransaction;

  const [form, setForm] = useState<FormState>({ ...initialForm });

  // Reset form when modal opens or editing transaction changes
  useEffect(() => {
    if (open) {
      setForm(editingTransaction ? transactionToForm(editingTransaction) : { ...initialForm });
    }
  }, [open, editingTransaction]);

  const set = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const handleSubmit = useCallback(() => {
    const amountCents = dollarsToCents(form.grossAmount);
    if (!form.transactionType || amountCents == null || amountCents <= 0 || !form.transactionDate) return;

    const feeCents = form.feeTaken ? dollarsToCents(form.feeTaken) : undefined;

    const payload: CreateTransactionRequest = {
      transactionType: form.transactionType as CreateTransactionRequest['transactionType'],
      amountCents,
      transactionDate: form.transactionDate,
      paymentMethod: (form.paymentMethod || undefined) as CreateTransactionRequest['paymentMethod'],
      referenceNumber: form.referenceNumber || undefined,
      payer: form.payer || undefined,
      payee: form.payees || undefined,
      status: (form.status || 'pending') as CreateTransactionRequest['status'],
      notes: undefined,
      feeCents,
    };

    if (isEdit && editingTransaction && onUpdate) {
      onUpdate(editingTransaction.id, payload as UpdateTransactionRequest, form.checkFile ?? undefined, form.removeExistingCheckFile);
    } else {
      onSubmit(payload, form.checkFile ?? undefined);
    }
  }, [form, isEdit, editingTransaction, onUpdate, onSubmit]);

  // Reset form when modal opens fresh
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        // Delay reset until after close animation
        setTimeout(() => setForm({ ...initialForm }), 200);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90dvh] md:max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {isEdit ? 'Edit Payment' : 'Add Payment'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Payment Type */}
          <div className="space-y-1">
            <Label className="text-xs">Payment Type</Label>
            <Select
              value={form.transactionType}
              onValueChange={(v) => set('transactionType', v)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="text-xs">
                    {PAYMENT_TYPE_LABELS[t] || labelFor(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Gross Amount + Fee Taken */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Gross Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="$0.00"
                className="h-8 text-xs"
                value={form.grossAmount}
                onChange={(e) => set('grossAmount', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fee Taken From This Payment</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="$0.00"
                className="h-8 text-xs"
                value={form.feeTaken}
                onChange={(e) => set('feeTaken', e.target.value)}
              />
            </div>
          </div>

          {/* Payer + Payees */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Payer</Label>
              <Input
                placeholder="e.g. State Farm"
                className="h-8 text-xs"
                value={form.payer}
                onChange={(e) => set('payer', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Payees</Label>
              <Input
                placeholder="e.g. John Doe, ABC Roofing"
                className="h-8 text-xs"
                value={form.payees}
                onChange={(e) => set('payees', e.target.value)}
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="space-y-1">
            <Label className="text-xs">Payment Method</Label>
            <Select
              value={form.paymentMethod || '__none__'}
              onValueChange={(v) =>
                set('paymentMethod', v === '__none__' ? '' : v)
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="text-xs">
                  Not specified
                </SelectItem>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">
                    {PAYMENT_METHOD_LABELS[m] || labelFor(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reference / Check Number */}
          <div className="space-y-1">
            <Label className="text-xs">Reference / Check Number</Label>
            <Input
              placeholder="Check #, ACH ref, etc."
              className="h-8 text-xs"
              value={form.referenceNumber}
              onChange={(e) => set('referenceNumber', e.target.value)}
            />
          </div>

          {/* Date + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={form.transactionDate}
                onChange={(e) => set('transactionDate', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => set('status', v)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">
                      {STATUS_LABELS[s] || labelFor(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Check File */}
          <div className="space-y-1">
            <Label className="text-xs">Check File</Label>
            {(() => {
              const hasNewFile = !!form.checkFile;
              const hasExistingFile = isEdit && !!editingTransaction?.checkFileBlobId && !hasNewFile && !form.removeExistingCheckFile;
              const displayName = hasNewFile
                ? form.checkFile!.name
                : hasExistingFile
                  ? (editingTransaction!.checkFileName || 'Uploaded file')
                  : null;

              return (
                <div className="flex items-center gap-1.5 w-full">
                  <label className="flex items-center gap-2 w-0 flex-1 cursor-pointer border rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/50 transition-colors overflow-hidden">
                    {displayName ? (
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <Upload className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="block truncate">{displayName || 'Choose file'}</span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setForm((prev) => ({ ...prev, checkFile: file, removeExistingCheckFile: false }));
                      }}
                    />
                  </label>
                  {(hasNewFile || hasExistingFile) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground"
                      onClick={() => {
                        if (hasNewFile) {
                          set('checkFile', null);
                        } else {
                          set('removeExistingCheckFile', true);
                        }
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={handleSubmit}
            disabled={isSubmitting || !form.grossAmount || parseFloat(form.grossAmount) <= 0 || !form.transactionDate}
          >
            {isSubmitting ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : null}
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
